/* eslint-disable max-len */
import { Mql, MqlScope } from '@deqo/mql-js';
import { Ace, Range } from 'ace-builds';
import { differenceInDays } from 'date-fns';
import { injectable } from 'inversify';
import { useMemo } from 'react';
import { Component, ComponentType, Manifest, ObjectManager } from '../../../concepts/objects';
import { GeomUtil } from '../../../platform/geom/GeomUtil';
import { Rect } from '../../../platform/geom/Rect';
import { Resolver, resolve, useResolve } from '../../Container';
import { resolvePhysicals } from '../../canvas/commands/CommandUtil';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { Tag } from '../annotations/Tag';
import { LensReflector } from './LensReflector';

// const useSuggestionScope = () => {
//     const reflector = useResolve(LensReflector);
//     const objects = useResolve(ObjectManager);
//     const mql = useResolve(EditorMqlEngine);

//     return {
//         suggestIdentifiers: (fragment: string) => {
//             if (fragment === '') {
//                 return [MqlSuggestionItem('type', 'property')];
//             }

//             const hits = mql.exec(fragment, Array.from(objects.getAll()));
//             const aspects = reflector.reflect(hits);

//             // const types = new Set(
//             //     hits.map(x => Component.describe(x))
//             // );

//             // const props = new Set(
//             //     Array.from(types).flatMap(t => t.properties.map(p => p.name))
//             // );

//             // const tags = new Set(
//             //     hits.flatMap(x => x.polarize(Tag)).map(x => x.name.toLowerCase())
//             // );

//             // const all = [
//             //     ...Array.from(props).map(x => MqlSuggestionItem(x, 'property')),
//             //     ...Array.from(tags).map(x => MqlSuggestionItem(x, 'assocation')),
//             // ];

//             return aspects
//                 .sort((a, b) => a.name.localeCompare(b.name))
//                 .map(x => MqlSuggestionItem(x.name, x.kind));
//         },
//         suggestValues: (identifier: string) => {
//             if (identifier === 'type') {
//                 return Manifest
//                     .getTypesByConvention(Physical)
//                     .map(x => `"${Component.describe(x).name}"`).sort()
//                     .map(x => MqlSuggestionItem(x, 'value'));
//             }
//             return [];
//         },
//     };
// };

// const fragment = model.getValueInRange({
//     startColumn: 0,
//     startLineNumber: 0,
//     endColumn: position.column,
//     endLineNumber: position.lineNumber,
// });

// const result = mql.suggest(fragment);
// const suggestions = (
//     (result.type === 'joiner' ? joinerSuggestions() : null) ??
//     (result.type === 'operator' ? operatorSuggestions() : null) ??
//     (result.type === 'identifier' ? identifierSuggestions(suggestionContext.current?.suggestIdentifiers(nearestWorkingFragment(fragment)) ?? []) : null) ??
//     (result.type === 'value' ? valueSuggestions(suggestionContext.current?.suggestValues(result.context) ?? []) : null) ??
//     []
// );

// return { suggestions };


const nearestWorkingMqlFragment = (fragment: string) => {
    while (fragment.length > 0) {
        try {
            Mql.compile(fragment);
            return fragment;
        }
        catch (e) {
            fragment = fragment.slice(0, -1);
        }
    }
    return '';
};

const completion = (value: string, meta: string, score: number, docs?: string): Ace.Completion => ({
    value,
    meta,
    score,
    docText: docs,
});

class MqlAceCompleter implements Ace.Completer {

    public readonly id = 'mql.completer';

    constructor(
        private objects: ObjectManager,
        private reflector: LensReflector,
        private scope: MqlScope,
    ) { }

    public getCompletions(
        editor: Ace.Editor,
        session: Ace.EditSession,
        position: Ace.Point,
        prefix: string,
        callback: Ace.CompleterCallback): void {

        const range = Range.fromPoints({ column: 0, row: 0 }, position);
        const fragment = session.getTextRange(range);

        const result = Mql.suggest(fragment);
        const suggestions = (
            (result.type === 'joiner' ? this.getJoinerSuggestions() : null) ??
            (result.type === 'operator' ? this.getOperatorSuggestions() : null) ??
            (result.type === 'identifier' ? this.getIdentifierSuggestions(fragment) : null) ??
            (result.type === 'value' ? this.getValueSuggestions(fragment, result.context) : null) ??
            []
        );

        callback(null, suggestions);
    }

    private getJoinerSuggestions = (): Ace.Completion[] =>
        Mql.tokens.joiners.map((t, i) => completion(t, 'keyword', 100 - i));

    private getOperatorSuggestions = (): Ace.Completion[] =>
        Mql.tokens.operators.map((t, i) => completion(t, 'operator', 100 - i));

    private getIdentifierSuggestions(fragment: string) {
        fragment = nearestWorkingMqlFragment(fragment);

        // Mql.tokens.operators.map((t, i) => completion(t, 'operator', i));

        if (fragment === '') {
            return [completion('type', 'property', 1)];
        }

        const hits = Mql.filter(this.scope, fragment, Array.from(this.objects.getAll()));
        const aspects = this.reflector.reflect(hits);

        return aspects
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(x => completion(x.name, x.kind, 10, x.description));
    }

    private getValueSuggestions(fragment: string, identifier: string) {
        fragment = nearestWorkingMqlFragment(fragment);

        if (identifier === 'type') {
            return Manifest
                .getTypesByConvention(Physical)
                .map(x => `"${Component.describe(x).name}"`).sort()
                .map(x => completion(x, 'value', 100));
        }

        const hits = Mql.filter(this.scope, fragment, Array.from(this.objects.getAll()));
        const aspect = this.reflector.reflect(hits).find(x => x.name === identifier);

        if (aspect && aspect.type === 'boolean') {
            return [
                completion('true', 'value', 100),
                completion('false', 'value', 100),
            ];
        }

        return [];
    }
}

@injectable()
export class MqlUtils {

    @resolve(LensReflector) private reflector: LensReflector;
    @resolve(ObjectManager) private objects: ObjectManager;
    @resolve(SpatialIndex) private spatial: SpatialIndex;
    @resolve(Resolver) private resolver: Resolver;

    public createScope(symbols?: Record<string, any>) {
        return MqlScope
            .createDefault()
            .addSymbols(symbols ?? {})
            .addFunctions({
                dateDiff: this.dateDiff,
                findElementId: this.findElementId,
                findNearestId: this.findNearestId,
                findParentId: this.getParentId,
                getParentId: this.getParentId,
                inside: this.inside,
                intersects: this.intersects,
                tagged: this.tagged,
            })
            .build();
    }

    public createCompleter() {
        return new MqlAceCompleter(this.objects, this.reflector, this.createScope());
    }

    private inside = (idOrNameOrElmt: any, elmt: any) => {
        if (!idOrNameOrElmt) return false;

        const eb = elmt.bounds(RelativeTo.Model);
        const containers = this.resolve(idOrNameOrElmt);
        for (const c of containers) {
            const cb = c.bounds(RelativeTo.Model);
            if (cb.contains(eb)) {
                return true;
            }
        }

        return false;
    };

    private intersects = (idOrNameOrElmt: any, elmt: any) => {
        if (!idOrNameOrElmt) return false;

        const eb = elmt.bounds(RelativeTo.Model);
        const containers = this.resolve(idOrNameOrElmt);
        for (const c of containers) {
            const cb = c.bounds(RelativeTo.Model);
            if (cb.intersects(eb)) {
                return true;
            }
        }

        return false;
    };

    private findNearestId = (idOrNameOrElmt: any, type: string) => {
        if (!type) return '';

        const [to] = this.resolve(idOrNameOrElmt);
        if (!to) return '';

        const typeDef = Manifest.describeComponent(type);
        if (!typeDef?.implements?.includes(Physical)) return '';

        const physType = typeDef.type as ComponentType<Physical>;

        // Start small, look in local area
        for (let i = 0; i < 5; i++) {

            const center = to.center(RelativeTo.Model);
            const area = Rect.fromCenter(center, 100 * i * 2);
            const hits = this.spatial.lookup(area, physType);
            if (!!hits.size) {
                const [nearest] = GeomUtil.sortByDistanceTo(center, Array.from(hits), x => x.center(RelativeTo.Model));
                if (nearest) return nearest.id;
            }
        }

        return '';
    };

    private findElementId = (type: string, criteria: string) => {
        if (!criteria) return '';
        criteria = criteria.trim().toLowerCase();

        const typeDef = Manifest.describeComponent(type);
        if (!typeDef) return '';

        const elmts = this.objects.getAll(typeDef.type);
        for (const e of elmts) {
            const p = e.as(Physical);
            if (!p) continue;

            for (const f of p.uiFields) {
                if (p.has(f.name)) {
                    const v = p.get(f.name)?.toString()?.trim()?.toLowerCase() || '';
                    if (v === criteria) return p.id;
                }
            }
        }

        return '';
    };

    private getParentId = (idOrNameOrElmt: any, type: string) => {
        if (!type) return '';
        if (!idOrNameOrElmt) return false;

        const [subject] = this.resolve(idOrNameOrElmt);
        if (subject?.alive && subject.mounted()) {
            for (let x = subject.parentPhysical; x !== null; x = x.parentPhysical) {
                if (x.type === type) {
                    return x.id;
                }
            }
        }

        return '';
    };

    private tagged = (...args: any[]) => {
        const checkValue = args.length === 3;
        const name = args[0];
        const value = args.length === 3 ? args[1] : undefined;
        const elmt = args.length === 3 ? args[2] : args[1];

        if (elmt instanceof Component) {
            for (const ch of elmt.children) {
                const t = ch.as(Tag);
                if (!t) continue;
                if (t.name !== name) continue;
                if (checkValue && t.value !== value) continue;
                return true;
            }
        }
        return false;
    };

    private dateDiff = (a: any, b: any) => {
        a = a instanceof Date ? a : Date.parse(a);
        b = b instanceof Date ? b : Date.parse(b);
        return differenceInDays(a, b);
    };

    private resolve = (input: any) => resolvePhysicals(this.resolver, input);
}

export const useMqlCompleter = () => {
    const utils = useResolve(MqlUtils);
    return useMemo(() => utils.createCompleter(), []);
};
